import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import CountryTimezoneSelector from '../components/CountryTimezoneSelector';
import { Controller } from 'react-hook-form';
import PhoneInput, { isValidPhoneNumber, getCountries, getCountryCallingCode } from 'react-phone-number-input';
import Input from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import './Signup.css';

const organizationTypes = [
  'Software Development Company',
  'IT Services & Consulting Firm',
  'Web Development Agency',
  'Mobile App Development Agency',
  'SaaS Product Company',
  'Digital Solutions Company',
  'Custom ERP/CRM Development Firm',
  'UI/UX Design + Development Studio',
  'IT Outsourcing / Contract Development Firm',
  'Startup Tech Team'
];

const ROLES = [
  'HR',
  'Operations Head',
  'Project Manager',
  'Project Lead',
  'Developer',
  'QA Engineer',
  'UI Engineer'
];

const Signup = () => {
  const { register, handleSubmit, formState: { errors }, setValue, control, trigger } = useForm({
    mode: 'onChange'
  });
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedMobileCountry, setSelectedMobileCountry] = useState('IN');
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    console.log('Submitting signup data:', data);

    try {
      const response = await authAPI.signup(data);
      console.log('Signup response:', response.data);
      login(response.data.token, response.data.user);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Signup error:', error);
      const errorMsg = error.response?.data?.message || 'Signup failed. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="form-wrapper">
          <div className="form-header">
            <div className="signup-logo">
              <svg viewBox="0 0 24 24" className="logo-icon">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>TaskManager</span>
            </div>
            <h1 className="form-title">Create Your Organization</h1>
            <p className="form-subtitle">Set up your workspace in just a few steps</p>
          </div>

          {error && <div className="error-banner">{error}</div>}

          <form onSubmit={handleSubmit(onSubmit)} className="signup-form">
            {/* Row 1: Organization Type & Name */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Organization Type</label>
                <select
                  {...register('organizationType', { required: 'Organization type is required' })}
                  className={`form-input ${errors.organizationType ? 'has-error' : ''}`}
                  disabled={loading}
                >
                  <option value="">Select Organization Type</option>
                  {organizationTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.organizationType && <span className="error-text">{errors.organizationType.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Organization Name</label>
                <input
                  {...register('organizationName', { required: 'Organization name is required' })}
                  className={`form-input ${errors.organizationName ? 'has-error' : ''}`}
                  placeholder="Enter your organization name"
                  disabled={loading}
                />
                {errors.organizationName && <span className="error-text">{errors.organizationName.message}</span>}
              </div>
            </div>

            {/* Row 2: Role & Full Name */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Select Your Role</label>
                <select
                  {...register('role', { required: 'Role is required' })}
                  className={`form-input ${errors.role ? 'has-error' : ''}`}
                  disabled={loading}
                >
                  <option value="">Choose your role</option>
                  {ROLES.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                {errors.role && <span className="error-text">{errors.role.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Your Full Name</label>
                <input
                  {...register('name', { required: 'Full name is required' })}
                  className={`form-input ${errors.name ? 'has-error' : ''}`}
                  placeholder="Enter your full name"
                  disabled={loading}
                />
                {errors.name && <span className="error-text">{errors.name.message}</span>}
              </div>
            </div>

            {/* Row 3: Username & Email */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  {...register('username', {
                    required: 'Username is required',
                    minLength: {
                      value: 3,
                      message: 'Username must be at least 3 characters'
                    },
                    pattern: {
                      value: /^[a-zA-Z0-9_-]+$/,
                      message: 'Username can only contain letters, numbers, underscores, and hyphens'
                    }
                  })}
                  className={`form-input ${errors.username ? 'has-error' : ''}`}
                  placeholder="Choose your username"
                  disabled={loading}
                />
                {errors.username && <span className="error-text">{errors.username.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Email Address (Optional)</label>
                <input
                  type="email"
                  {...register('email', {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className={`form-input ${errors.email ? 'has-error' : ''}`}
                  placeholder="Enter your email (optional)"
                  disabled={loading}
                />
                {errors.email && <span className="error-text">{errors.email.message}</span>}
              </div>
            </div>

            {/* Row 4: Mobile */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mobile Number</label>
                <div className={`premium-phone-input ${errors.mobile ? 'has-error' : ''}`}>
                  <div className="country-select-wrapper">
                    {selectedMobileCountry ? (
                      <>
                        <img
                          src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedMobileCountry}.svg`}
                          alt={selectedMobileCountry}
                          className="flag-icon"
                        />
                        <span className="calling-code">+{getCountryCallingCode(selectedMobileCountry)}</span>
                      </>
                    ) : (
                      <div className="globe-icon">🌐</div>
                    )}
                    <select
                      className="country-native-select"
                      value={selectedMobileCountry || ''}
                      onChange={(e) => {
                        const newCountry = e.target.value || null;
                        setSelectedMobileCountry(newCountry);
                        if (newCountry) {
                          // Clear previous mobile value error if switching country
                          trigger('mobile');
                        }
                      }}
                      disabled={loading}
                    >
                      <option value="">Select Country</option>
                      {getCountries().map((country) => (
                        <option key={country} value={country}>
                          {en[country]} (+{getCountryCallingCode(country)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="phone-input-field-wrapper">
                    <Controller
                      name="mobile"
                      control={control}
                      rules={{
                        required: 'Mobile number is required',
                        validate: (value) => {
                          if (!value) return true;
                          return isValidPhoneNumber(value) || 'Invalid phone number for the selected country';
                        }
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Input
                          country={selectedMobileCountry}
                          placeholder={selectedMobileCountry ? `Enter number for ${en[selectedMobileCountry]}` : "Select country first"}
                          value={value}
                          onChange={onChange}
                          disabled={!selectedMobileCountry || loading}
                          className="PhoneInputInput"
                        />
                      )}
                    />
                  </div>
                </div>
                {errors.mobile && <span className="error-text">{errors.mobile.message}</span>}
              </div>
            </div>

            {/* Row 5: Password & Country */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters'
                      }
                    })}
                    className={`form-input ${errors.password ? 'has-error' : ''}`}
                    placeholder="Create a strong password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password.message}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Country</label>
                <CountryTimezoneSelector
                  selectedCountry={selectedCountry}
                  selectedTimezone={selectedTimezone}
                  onCountryChange={(country) => {
                    setSelectedCountry(country);
                    setValue('country', country, { shouldValidate: true });
                  }}
                  onTimezoneChange={(timezone) => {
                    setSelectedTimezone(timezone);
                    setValue('timezone', timezone, { shouldValidate: true });
                  }}
                  isCountryOnly={true}
                />
                <input type="hidden" {...register('country', { required: 'Country is required' })} />
                {errors.country && <span className="error-text">{errors.country.message}</span>}
              </div>
            </div>

            {/* Row 6: Timezone Full Width */}
            <div className="form-row full-width">
              <div className="form-group">
                <label className="form-label">Timezone</label>
                <CountryTimezoneSelector
                  selectedCountry={selectedCountry}
                  selectedTimezone={selectedTimezone}
                  onCountryChange={(country) => {
                    setSelectedCountry(country);
                    setValue('country', country, { shouldValidate: true });
                  }}
                  onTimezoneChange={(timezone) => {
                    setSelectedTimezone(timezone);
                    setValue('timezone', timezone, { shouldValidate: true });
                  }}
                  isTimezoneOnly={true}
                />
                <input type="hidden" {...register('timezone', { required: 'Timezone is required' })} />
                {errors.timezone && <span className="error-text">{errors.timezone.message}</span>}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>

            {/* Login Link */}
            <div className="login-link">
              <p>Already have an account? <Link to="/login">Sign in</Link></p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
